import React, { useState } from 'react';
import {
  Box, Card, Typography, Avatar, Tooltip, useTheme, alpha,
  Accordion, AccordionSummary, AccordionDetails, IconButton
} from '@mui/material';
import { Code as CodeIcon, ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
// Ensure this path is correct: It assumes clipboardUtils.js is in PROJECT_ROOT/src/utils/
import { copyToClipboard } from '../../../utils/clipboardUtils';

const ToolCallMessage = ({ toolName, args, timestamp, toolCallId }) => {
  const theme = useTheme();
  const [userHasToggledExpansion, setUserHasToggledExpansion] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(date);
  };

  const argsString = JSON.stringify(args || {}, null, 2);
  const isArgsLarge = argsString.length > 200 || argsString.split('\n').length > 7;

  const isEffectivelyExpanded = !isArgsLarge || userHasToggledExpansion;

  const handleAccordionChange = (event, newExpandedState) => {
    if (isArgsLarge) {
      setUserHasToggledExpansion(newExpandedState);
    }
  };

  const handleCopyArgs = (e) => {
    e.stopPropagation();
    copyToClipboard(argsString, 'Tool arguments copied!');
  };

  return (
    <Box sx={{ display: 'flex', alignSelf: 'flex-start', maxWidth: '80%', pl: { xs: 2, sm: 5 }, mt: 0.5, mb: 0.5 }}>
      <Box sx={{ mr: 1, mt: 0.5 }}>
        <Tooltip title={`Tool Call: ${toolName}${toolCallId ? ` (ID: ${toolCallId})` : ''}`}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.info.dark, 0.7), width: 24, height: 24 }}>
            <CodeIcon fontSize="inherit" />
          </Avatar>
        </Tooltip>
      </Box>
      <Card sx={{
        backgroundColor: alpha(theme.palette.info.main, 0.05),
        borderRadius: 1,
        flexGrow: 1,
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
      }}>
        <Accordion
          expanded={isEffectivelyExpanded}
          onChange={handleAccordionChange}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: 'transparent',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={isArgsLarge ? <ExpandMoreIcon /> : null}
            aria-controls={`tool-call-args-content-${toolCallId || toolName}-${timestamp}`} // Added timestamp for more uniqueness
            id={`tool-call-args-header-${toolCallId || toolName}-${timestamp}`} // Added timestamp for more uniqueness
            sx={{
              py: 0, px: 1.5, minHeight: 'auto',
              '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
              cursor: isArgsLarge ? 'pointer' : 'default',
            }}
            onClick={!isArgsLarge ? (e) => e.stopPropagation() : undefined}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box sx={{ overflow: 'hidden', pr: 1 }}>
                <Typography variant="caption" color="info.dark" fontWeight="bold" noWrap component="span">
                  Tool Call: {toolName}
                </Typography>
                {toolCallId && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5, fontStyle: 'italic' }} component="span">
                    (ID: {toolCallId})
                  </Typography>
                )}
                {timestamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', ml: 1 }} component="span">
                    ({formatDate(timestamp)})
                  </Typography>
                )}
              </Box>
              {isEffectivelyExpanded && (
                <Tooltip title="Copy Arguments">
                  <IconButton
                    size="small"
                    onClick={handleCopyArgs}
                    aria-label="Copy tool arguments"
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          {isEffectivelyExpanded && (
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5, borderTop: `1px solid ${alpha(theme.palette.info.main, 0.1)}` }}>
              <SyntaxHighlighter
                style={vs2015}
                language="json"
                customStyle={{
                  fontSize: '0.8rem',
                  margin: '0.5rem 0 0 0',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  backgroundColor: 'transparent'
                }}
                wrapLines={true}
                PreTag="div"
              >
                {argsString}
              </SyntaxHighlighter>
            </AccordionDetails>
          )}
        </Accordion>
      </Card>
    </Box>
  );
};

export default ToolCallMessage;